import { Link } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * One number, and, where there is a page behind it, the way in.
 *
 * The console used to spend a whole destination on each of these. "Jobs",
 * "Agents" and "All steps" sat in the rail as peers of the gate list, so a
 * person landing on the hub had four places to look before they could look at
 * anything, and three of the four were answering a question they had not asked
 * yet: how many jobs are open, how many machines are up, how many steps are
 * running.
 *
 * A tile answers that question in one glance, and where `to` is given it *is*
 * the way in to the page behind it: the pages did not go away and their routes
 * did not change, what went away is having to choose between four front doors.
 * `/me` and `/admin` (issue #87) draw the same shape for figures that are not
 * destinations of their own -- the account card's Agents/Jobs/Tasks count, the
 * admin band's five operational figures -- so `to` is optional: without it the
 * tile renders as a plain, unclickable surface, with no arrow and no lift,
 * because a card that does not go anywhere should not promise that it does.
 *
 * So the whole tile is the link when it has one, not a chevron in the corner of
 * it. A card that navigates and a card that reports look identical at rest,
 * which is why an interactive one lifts on hover and carries an arrow: both are
 * saying "this goes somewhere".
 *
 * `layout="row"` is the same tile laid short. Four of these read as a band
 * across the top of the page rather than as a column beside the gate list, and
 * a band that pushes the gates below the fold has cost more than it told. So
 * the figure and its note share one baseline, the note clips instead of
 * wrapping, and the figure steps down to 28px: in a band the number is context
 * for the gates, not the subject of the page.
 */
export function StatTile({
  label,
  value,
  note,
  icon: Icon,
  to,
  search,
  tone = 'var(--dy-accent)',
  /** Draw the figure in `tone` as well as the mark. For the number that matters. */
  loud = false,
  /** Something a person has to act on: the mark and figure draw in the warn
      hue regardless of `tone` or `loud`. For a static tile reporting a stale
      or stranded state (the admin band's Devices online, Queued, Failed). */
  alarm = false,
  className,
  /** `'row'` lays the tile short for the horizontal metric band. */
  layout = 'stacked',
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  /** A lucide icon, or anything else that takes a `className`: Claude's own
      mark is not a lucide icon and the Agents tile wears it. */
  icon: ComponentType<{ className?: string }>;
  /** Omit for a tile that only reports, with no page of its own to open. */
  to?: string;
  search?: Record<string, string>;
  tone?: string;
  loud?: boolean;
  alarm?: boolean;
  className?: string;
  layout?: 'stacked' | 'row';
}) {
  const row = layout === 'row';
  const interactive = Boolean(to);
  const markTone = alarm ? 'var(--dy-warn)' : tone;
  const figureTone = alarm || loud ? markTone : undefined;

  // Identical whether the tile navigates or not: the mark, the label, and the
  // arrow that says the whole card goes somewhere, drawn only when it does.
  const head = (
    <div className="flex items-center gap-2.5">
      <span
        className="grid size-7 shrink-0 place-items-center rounded-lg"
        style={{ background: `color-mix(in srgb, ${markTone} 14%, transparent)`, color: markTone }}
        aria-hidden
      >
        <Icon className="size-[15px]" />
      </span>
      <span className="dy-eyebrow min-w-0 flex-1 truncate">{label}</span>
      {interactive ? (
        <ArrowUpRight
          className="text-fg-3 group-hover:text-foreground size-3.5 shrink-0 transition-colors duration-100"
          aria-hidden
        />
      ) : null}
    </div>
  );

  const body = row ? (
    <div className="mt-1.5 flex items-baseline gap-2">
      <span
        className="dy-figure whitespace-nowrap text-2xl leading-none"
        style={figureTone ? { color: figureTone } : undefined}
      >
        {value}
      </span>
      {note ? <span className="text-fg-3 min-w-0 truncate text-xs">{note}</span> : null}
    </div>
  ) : (
    <>
      <div
        className="dy-figure mt-2 text-3xl leading-none"
        style={figureTone ? { color: figureTone } : undefined}
      >
        {value}
      </div>
      {note ? <div className="text-fg-3 mt-1.5 truncate text-xs">{note}</div> : null}
    </>
  );

  const shared = cn('dy-glass relative block', row ? 'px-3.5 py-3' : 'px-4 py-3.5', className);

  if (!to) {
    return (
      <div className={shared}>
        {head}
        {body}
      </div>
    );
  }

  return (
    <Link
      // The route tree is typed and these are string literals from the caller;
      // the alternative is four near-identical components, one per destination.
      to={to as never}
      search={search as never}
      className={cn(
        shared,
        'dy-tile group',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dy-accent)]',
      )}
    >
      {head}
      {body}
    </Link>
  );
}
